import { List, Plus } from "@phosphor-icons/react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useBranding } from "@/lib/branding";
import NotificationBell from "@/components/NotificationBell";
import PhoneMessagesIndicator from "@/components/PhoneMessagesIndicator";
import QuickCalculator from "@/components/QuickCalculator";
import UserProfileMenu from "@/components/UserProfileMenu";

// DSG-001 compliant — all colours resolved via semantic Tailwind tokens.
export default function Header({ onMenuClick }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { branding, host, primaryLogoSrc } = useBranding();
  const canCreate = user && ["Administrator", "Office Staff", "Owner"].includes(user.role);
  const displayName = branding?.identity?.display_name || "Coolman Fuels";
  // DSG-006 · title / subtitle now driven by the Host Application record.
  // `branding.identity.tagline` remains as an editorial override — if an
  // admin has set a company-level tagline it wins over the host subtitle.
  const appTitle = host?.name || "Fuel Order Centre";
  const subtitle =
    branding?.identity?.tagline
    || host?.subtitle
    || `${displayName} · Internal Staff Portal`;
  return (
    <header
      data-testid="app-header"
      className="sticky top-0 z-30 flex h-20 items-center gap-3 border-b border-divider bg-surface px-4 sm:px-8"
    >
      <button
        data-testid="header-menu-button"
        onClick={onMenuClick}
        className="rounded-md p-2 text-ink-secondary hover:bg-surface-alt lg:hidden"
        aria-label="Open menu"
      >
        <List size={24} weight="bold" />
      </button>

      <div className="flex items-center gap-3 lg:hidden">
        <Link
          to="/"
          data-testid="header-logo-link-mobile"
          aria-label="Go to dashboard"
          title="Go to dashboard"
          className="focus:outline-none focus-visible:ring-2 focus-visible:ring-brand rounded-md"
        >
          <img src={primaryLogoSrc()} alt={displayName} className="h-10 w-auto" data-testid="header-logo-mobile" />
        </Link>
      </div>

      <div className="hidden lg:block">
        <Link
          to="/"
          data-testid="header-title-link"
          aria-label="Go to dashboard"
          title="Go to dashboard"
          className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-brand rounded-md"
        >
          <h1 className="font-display text-xl font-semibold text-charcoal" data-testid="header-app-title">{appTitle}</h1>
          <div className="text-sm text-ink-muted" data-testid="header-app-subtitle">{subtitle}</div>
        </Link>
      </div>

      <div className="ml-auto flex items-center gap-2">
        {canCreate && (
          <>
            <button
              data-testid="header-new-order-button"
              onClick={() => navigate("/new-order")}
              className="inline-flex h-12 items-center gap-2 rounded-md bg-brand px-5 text-base font-semibold text-brand-foreground shadow-token-sm hover:bg-brand-hover"
            >
              <Plus size={18} weight="bold" />
              New Order
            </button>
            <button
              data-testid="header-new-phone-message-button"
              onClick={() => navigate("/messages?new=1")}
              className="inline-flex h-12 items-center gap-2 rounded-md bg-[color:var(--color-info)] hover:bg-[color:var(--color-info-fg)] px-5 text-base font-semibold text-white shadow-token-sm transition-colors"
              title="Capture a new phone message"
            >
              <Plus size={18} weight="bold" />
              New Message
            </button>
          </>
        )}
        {user && (
          <div className="flex items-center gap-1 border-l border-divider pl-2">
            <QuickCalculator />
            <PhoneMessagesIndicator />
            <NotificationBell />
            <UserProfileMenu />
          </div>
        )}
      </div>
    </header>
  );
}
